import { Modal } from "@mantine/core";
import React, { useEffect, useState } from "react";
import { BiLoaderAlt } from "react-icons/bi";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { addCampaign } from "../../stores/actions/campaign.actions.types";
import { connect } from "react-redux";
import { emailTemplateList } from "../../stores/actions/emailtemplate.actions.types";
import { TextAreaInput, TextInput } from "../UI/FormField";
import EmailTemplateSelect from "../Common/EmailTemplateSelect";

const AddEmailCampaign = ({
  emailTemplateList,
  addCampaign,
  reload,
  setReload,
  type,
}) => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [emailloading, setEmailLoading] = useState(true);

  const validationSchema = Yup.object({
    name: Yup.string().required("Campaign name is required"),
    description: Yup.string(),
    emailTemplateId: Yup.string().required("Email template is required"),
    targetedAudience: Yup.string().required("Target audience is required"),
    status: Yup.string()
      .oneOf(["draft", "scheduled", "sent", "paused", "failed"])
      .required(),
  });

  const close = () => setIsModalOpen(false);

  useEffect(() => {
    setEmailLoading(true);
    emailTemplateList({
      data: { size: 10000 },
      callback: (res) => {
        if (res.success) {
          setTemplates(res?.data?.emailTemplates || []);
        }
        setEmailLoading(false);
      },
    });
  }, [emailTemplateList, isModalOpen]);

  const handleSubmit = (values) => {
    setLoading(true);
    const callback = (data) => {
      setLoading(false);
      if (data.success) {
        setIsModalOpen(false);
        setReload(!reload);
      }
    };

    const request = { data: { ...values, type }, callback };

    addCampaign(request);
  };

  return (
    <div>
      <button
        onClick={() => setIsModalOpen(true)}
        className='mt-4 block w-[200px] bg-primary text-white py-2 px-5 rounded-lg text-center'
      >
        Add Email Campaign
      </button>

      <Modal
        centered
        size='lg'
        opened={isModalOpen}
        onClose={close}
        title='Add Email Campaign'
        overlayProps={{ backgroundOpacity: 0.1 }}
      >
        <div className='max-h-96 overflow-y-scroll'>
          <Formik
            initialValues={{
              name: "",
              description: "",
              emailTemplateId: "",
              targetedAudience: "",
              status: "draft",
            }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ errors, touched, setFieldValue, values }) => (
              <Form className='space-y-4'>
                <div>
                  <TextInput
                    label={"Campaign Name"}
                    name='name'
                    placeholder='Campaign Name'
                  />
                </div>

                <div>
                  <TextAreaInput
                    name='description'
                    label={"Description"}
                    placeholder='Description'
                    className='block w-full rounded-md border px-3 py-2'
                  />
                </div>

                <EmailTemplateSelect
                  templates={templates}
                  value={values.emailTemplateId}
                  setFieldValue={setFieldValue}
                />
                {type === "cart-funnel-email" || type === "pending-email" ? (
                  ""
                ) : (
                  <div>
                    <label className='block mb-1 font-medium'>
                      Target Audience
                    </label>
                    <div className='flex gap-2 text-sm'>
                      {["customers", "subscribers", "outreach", "all"].map(
                        (audience) => {
                          const selected = values.targetedAudience === audience;
                          const bgClass = selected
                            ? "bg-primary text-white"
                            : "bg-gray-200 text-gray-700";
                          return (
                            <button
                              type='button'
                              key={audience}
                              onClick={() =>
                                setFieldValue("targetedAudience", audience)
                              }
                              className={`px-3 py-1 text-sm rounded-full font-medium transition-colors duration-200 ${bgClass}`}
                            >
                              {audience.charAt(0).toUpperCase() +
                                audience.slice(1)}
                            </button>
                          );
                        },
                      )}
                    </div>
                    {errors.targetedAudience && touched.targetedAudience && (
                      <div className='text-red-500 text-sm mt-1'>
                        {errors.targetedAudience}
                      </div>
                    )}
                  </div>
                )}

                <div className='flex gap-2 justify-end mt-4'>
                  <button
                    type='submit'
                    disabled={loading}
                    className='bg-primary w-[150px] px-4 py-2 rounded text-white flex items-center justify-center'
                  >
                    {loading ? (
                      <BiLoaderAlt className='animate-spin m-auto' />
                    ) : (
                      "Add Campaign"
                    )}
                  </button>
                  <button
                    type='button'
                    onClick={close}
                    className='bg-gray-200 px-4 py-2 rounded text-gray-700'
                  >
                    Cancel
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </Modal>
    </div>
  );
};

const mapStateToProps = (state) => ({
  loading: state?.app?.visible,
});

export default connect(mapStateToProps, { addCampaign, emailTemplateList })(
  AddEmailCampaign,
);
